/**
 * Character Builder Session Storage
 * Manages localStorage persistence for character editing sessions
 * Tracks image state history and restores on page refresh
 */

import { Character2DLayered, LayerStep } from '../types';

// ============================================
// TYPES
// ============================================

export interface LayerEditHistoryEntry {
  url: string;
  timestamp: number;
  type: 'initial' | 'generated' | 'edited' | 'regenerated';
  editDetails?: {
    brushStrokes?: number;
    eraseStrokes?: number;
  };
}

export interface LayerImageState {
  step: LayerStep;
  layerType: 'body' | 'head';

  // Full edit history (for localStorage - allows undo/recovery)
  history: LayerEditHistoryEntry[];

  // Current index in history
  currentHistoryIndex: number;

  // Quick access to current state
  currentUrl: string;

  // Last committed state (saved to R2)
  // This is the final image: last regenerated + any drawings/erasings on top
  committedUrl?: string;
  committedAt?: number;

  // Metadata
  lastUpdated: number;
  isDirty: boolean;             // Has unsaved edits since last commit
}

export interface CharacterSession {
  characterId: string;
  projectId: string;
  userId: string;
  characterName?: string; // Character name for display

  // Layer states (keyed by step number)
  layerStates: Record<number, LayerImageState>;

  // Workflow state
  currentStep: LayerStep;
  completedSteps: LayerStep[];

  // Session metadata
  sessionId: string;
  lastSaved: number;
  isFinalized: boolean;

  // UI state to preserve on resume
  showCompositor?: boolean; // True when user was in compositor view
}

// ============================================
// STORAGE KEYS
// ============================================

const STORAGE_PREFIX = 'ezart_character_session_';

function getSessionKey(characterId: string): string {
  return `${STORAGE_PREFIX}${characterId}`;
}

function getAllSessionKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
}

// ============================================
// SESSION MANAGEMENT
// ============================================

/**
 * Create a new session for a character
 */
export function createCharacterSession(
  characterId: string,
  projectId: string,
  userId: string
): CharacterSession {
  const session: CharacterSession = {
    characterId,
    projectId,
    userId,
    layerStates: {},
    currentStep: 1,
    completedSteps: [],
    sessionId: `session_${Date.now()}`,
    lastSaved: Date.now(),
    isFinalized: false,
  };

  saveSession(session);
  return session;
}

/**
 * Save session to localStorage
 */
export function saveSession(session: CharacterSession): void {
  try {
    const key = getSessionKey(session.characterId);
    localStorage.setItem(key, JSON.stringify(session));
  } catch (error) {
    console.error('Failed to save character session:', error);
  }
}

/**
 * Load session from localStorage
 */
export function loadSession(characterId: string): CharacterSession | null {
  try {
    const key = getSessionKey(characterId);
    const data = localStorage.getItem(key);
    if (!data) return null;

    return JSON.parse(data) as CharacterSession;
  } catch (error) {
    console.error('Failed to load character session:', error);
    return null;
  }
}

/**
 * Delete session from localStorage
 */
export function deleteSession(characterId: string): void {
  try {
    const key = getSessionKey(characterId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to delete character session:', error);
  }
}

/**
 * Clear all old sessions (older than specified days)
 */
export function cleanupOldSessions(daysOld: number = 7): void {
  try {
    const allKeys = getAllSessionKeys();
    const cutoffDate = Date.now() - daysOld * 24 * 60 * 60 * 1000;

    allKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        const session = JSON.parse(data) as CharacterSession;
        if (session.lastSaved < cutoffDate) {
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('Failed to cleanup old sessions:', error);
  }
}

/**
 * Get all active sessions for a user
 */
export function getAllUserSessions(userId: string): CharacterSession[] {
  try {
    const allKeys = getAllSessionKeys();
    const sessions: CharacterSession[] = [];

    allKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        const session = JSON.parse(data) as CharacterSession;
        if (session.userId === userId && !session.isFinalized) {
          sessions.push(session);
        }
      }
    });

    // Sort by lastSaved (most recent first)
    return sessions.sort((a, b) => b.lastSaved - a.lastSaved);
  } catch (error) {
    console.error('Failed to get user sessions:', error);
    return [];
  }
}

/**
 * Get sessions for a specific project
 */
export function getProjectSessions(projectId: string): CharacterSession[] {
  try {
    const allKeys = getAllSessionKeys();
    const sessions: CharacterSession[] = [];

    allKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        const session = JSON.parse(data) as CharacterSession;
        if (session.projectId === projectId && !session.isFinalized) {
          sessions.push(session);
        }
      }
    });

    // Sort by lastSaved (most recent first)
    return sessions.sort((a, b) => b.lastSaved - a.lastSaved);
  } catch (error) {
    console.error('Failed to get project sessions:', error);
    return [];
  }
}

// ============================================
// LAYER STATE MANAGEMENT
// ============================================

const MAX_HISTORY_ENTRIES = 10; // Limit history to prevent localStorage overflow

/**
 * Initialize a new layer state with initial image
 */
export function initializeLayerState(
  session: CharacterSession,
  step: LayerStep,
  layerType: 'body' | 'head',
  initialUrl: string
): CharacterSession {
  const initialEntry: LayerEditHistoryEntry = {
    url: initialUrl,
    timestamp: Date.now(),
    type: 'initial',
  };

  const state: LayerImageState = {
    step,
    layerType,
    history: [initialEntry],
    currentHistoryIndex: 0,
    currentUrl: initialUrl,
    lastUpdated: Date.now(),
    isDirty: false,
  };

  session.layerStates[step] = state;
  session.lastSaved = Date.now();
  saveSession(session);
  return session;
}

/**
 * Add a new entry to layer history (with limit to prevent localStorage overflow)
 */
function addToHistory(
  state: LayerImageState,
  entry: LayerEditHistoryEntry
): void {
  // Trim history if we're at the limit
  if (state.history.length >= MAX_HISTORY_ENTRIES) {
    // Keep the first entry (initial) and the most recent ones
    state.history = [
      state.history[0], // Always keep initial
      ...state.history.slice(-(MAX_HISTORY_ENTRIES - 2))
    ];
  }

  // Add new entry
  state.history.push(entry);
  state.currentHistoryIndex = state.history.length - 1;
  state.currentUrl = entry.url;
  state.lastUpdated = Date.now();
}

/**
 * Update layer state after manual edit (drawing/erasing)
 * Stores in localStorage for undo capability
 */
export function updateLayerWithEdit(
  session: CharacterSession,
  step: LayerStep,
  editedUrl: string,
  editDetails?: { brushStrokes?: number; eraseStrokes?: number }
): CharacterSession {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  const editEntry: LayerEditHistoryEntry = {
    url: editedUrl,
    timestamp: Date.now(),
    type: 'edited',
    editDetails,
  };

  addToHistory(state, editEntry);
  state.isDirty = true;

  session.lastSaved = Date.now();
  saveSession(session);
  return session;
}

/**
 * Update layer state after regeneration (image-to-image from edited)
 * This creates a clean baseline for further edits
 */
export function updateLayerWithRegeneration(
  session: CharacterSession,
  step: LayerStep,
  regeneratedUrl: string
): CharacterSession {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  const regenEntry: LayerEditHistoryEntry = {
    url: regeneratedUrl,
    timestamp: Date.now(),
    type: 'regenerated',
  };

  addToHistory(state, regenEntry);
  state.isDirty = false; // Regeneration produces clean state

  session.lastSaved = Date.now();
  saveSession(session);
  return session;
}

/**
 * Navigate to a previous state in history (undo)
 */
export function undoLayerEdit(
  session: CharacterSession,
  step: LayerStep
): CharacterSession {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  if (state.currentHistoryIndex > 0) {
    state.currentHistoryIndex -= 1;
    state.currentUrl = state.history[state.currentHistoryIndex].url;
    state.isDirty = true;
    state.lastUpdated = Date.now();

    session.lastSaved = Date.now();
    saveSession(session);
  }

  return session;
}

/**
 * Navigate to a later state in history (redo)
 */
export function redoLayerEdit(
  session: CharacterSession,
  step: LayerStep
): CharacterSession {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  if (state.currentHistoryIndex < state.history.length - 1) {
    state.currentHistoryIndex += 1;
    state.currentUrl = state.history[state.currentHistoryIndex].url;
    state.isDirty = true;
    state.lastUpdated = Date.now();

    session.lastSaved = Date.now();
    saveSession(session);
  }

  return session;
}

/**
 * Commit layer state (mark as complete and save final URL to R2)
 * This is called when the user clicks "Complete" on a layer
 * The committedUrl will be saved to R2 permanent storage
 */
export function commitLayerState(
  session: CharacterSession,
  step: LayerStep,
  committedUrl: string
): CharacterSession {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  // Save the committed state (this will be uploaded to R2)
  state.committedUrl = committedUrl;
  state.committedAt = Date.now();
  state.isDirty = false;
  state.lastUpdated = Date.now();

  // Add to completed steps if not already there
  if (!session.completedSteps.includes(step)) {
    session.completedSteps.push(step);
    session.completedSteps.sort();
  }

  session.lastSaved = Date.now();
  saveSession(session);
  return session;
}

/**
 * Get the final committed state for upload to R2
 * Returns the current URL (last regenerated + any manual edits)
 */
export function getCommittableState(
  session: CharacterSession,
  step: LayerStep
): string | null {
  const state = session.layerStates[step];
  if (!state) return null;

  // Return current URL (which represents the latest state)
  return state.currentUrl;
}

/**
 * Set a specific history entry as the current state
 * Used when user navigates to a specific point in history and wants to commit it
 */
export function setCurrentStateFromHistory(
  session: CharacterSession,
  step: LayerStep,
  historyIndex: number
): CharacterSession {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  if (historyIndex < 0 || historyIndex >= state.history.length) {
    throw new Error(`Invalid history index: ${historyIndex}`);
  }

  // Set current state to the selected history entry
  state.currentHistoryIndex = historyIndex;
  state.currentUrl = state.history[historyIndex].url;
  state.isDirty = false; // Selecting from history is a clean state
  state.lastUpdated = Date.now();

  session.lastSaved = Date.now();
  saveSession(session);
  return session;
}

/**
 * Upload current state to R2 (temp storage) and update the URL
 * Used when navigating history and need to ensure the image is in R2
 * Returns the new R2 URL
 */
export async function uploadCurrentStateToR2(
  session: CharacterSession,
  step: LayerStep,
  uploadFn: (blob: Blob) => Promise<string>
): Promise<string> {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  const currentUrl = state.currentUrl;

  // If already an R2 URL (not blob/data), return as-is
  if (!currentUrl.startsWith('blob:') && !currentUrl.startsWith('data:') && currentUrl.includes('http')) {
    return currentUrl;
  }

  // Convert blob/data URL to blob
  const response = await fetch(currentUrl);
  const blob = await response.blob();

  // Upload to R2 temp storage
  const r2Url = await uploadFn(blob);

  // Update current entry in history with R2 URL
  const currentEntry = state.history[state.currentHistoryIndex];
  currentEntry.url = r2Url;
  state.currentUrl = r2Url;
  state.lastUpdated = Date.now();

  session.lastSaved = Date.now();
  saveSession(session);

  return r2Url;
}

/**
 * Get the current (final) state of a layer
 * Priority: regenerated > edited > initial
 */
export function getLayerCurrentState(
  session: CharacterSession,
  step: LayerStep
): LayerImageState | null {
  return session.layerStates[step] || null;
}

/**
 * Get all layer states (for session recovery)
 */
export function getAllLayerStates(
  session: CharacterSession
): Record<number, LayerImageState> {
  return session.layerStates;
}

// ============================================
// SESSION SYNC WITH CHARACTER DATA
// ============================================

/**
 * Initialize session from existing character
 */
export function initializeSessionFromCharacter(
  character: Character2DLayered
): CharacterSession {
  const session = createCharacterSession(
    character.id,
    character.project_id,
    character.user_id
  );

  session.currentStep = character.current_step;
  session.completedSteps = character.completed_steps;
  session.isFinalized = character.is_finalized;

  // Initialize layer states from character data
  if (character.core_layers.body) {
    initializeLayerState(
      session,
      1,
      'body',
      character.core_layers.body.rendered_url
    );
  }

  if (character.core_layers.head) {
    initializeLayerState(
      session,
      2,
      'head',
      character.core_layers.head.rendered_url
    );
  }

  saveSession(session);
  return session;
}

/**
 * Update session workflow state
 */
export function updateSessionWorkflow(
  session: CharacterSession,
  currentStep: LayerStep,
  completedSteps: LayerStep[]
): CharacterSession {
  session.currentStep = currentStep;
  session.completedSteps = completedSteps;
  session.lastSaved = Date.now();

  saveSession(session);
  return session;
}

/**
 * Mark session as finalized
 */
export function finalizeSession(
  session: CharacterSession
): CharacterSession {
  session.isFinalized = true;
  session.lastSaved = Date.now();

  saveSession(session);
  return session;
}

// ============================================
// HELPER UTILITIES
// ============================================

/**
 * Check if a URL is temporary (should be moved to permanent storage)
 */
export function isTempUrl(url: string): boolean {
  return url.includes('character-temp') || url.startsWith('blob:') || url.startsWith('data:');
}

/**
 * Get layer type from step number
 */
export function getLayerTypeFromStep(step: LayerStep): 'body' | 'head' {
  switch (step) {
    case 1: return 'body';
    case 2: return 'head';
    default: return 'body';
  }
}

/**
 * Ensure current state is in R2 temp storage
 * Automatically uploads blob/data URLs to R2 and updates the history
 * This should be called before committing a layer or when switching layers
 */
export async function ensureStateInR2(
  session: CharacterSession,
  step: LayerStep,
  uploadToTempFn: (blob: Blob, filename: string) => Promise<string>
): Promise<CharacterSession> {
  const state = session.layerStates[step];
  if (!state) {
    throw new Error(`No layer state found for step ${step}`);
  }

  const currentUrl = state.currentUrl;

  // If already an R2 URL, no action needed
  if (!currentUrl.startsWith('blob:') &&
      !currentUrl.startsWith('data:') &&
      (currentUrl.includes('r2.') || currentUrl.includes('cloudflare'))) {
    return session;
  }

  // Need to upload to R2 temp storage
  const response = await fetch(currentUrl);
  const blob = await response.blob();
  const layerType = getLayerTypeFromStep(step);
  const filename = `${layerType}_temp_${Date.now()}.png`;

  // Upload to temp storage
  const r2Url = await uploadToTempFn(blob, filename);

  // Update current history entry
  const currentEntry = state.history[state.currentHistoryIndex];
  currentEntry.url = r2Url;
  state.currentUrl = r2Url;
  state.lastUpdated = Date.now();

  session.lastSaved = Date.now();
  saveSession(session);

  return session;
}
